/**
 * Eigen Verlof Widget (Epic 5, Story 5.1 — Rolgebaseerd dashboard, UX V7)
 *
 * Medewerker-sectie (altijd zichtbaar): compacte 4-tegels eigen
 * verlofsaldo + mini-lijst status eigen aanvragen, doorklik naar
 * "Mijn verlof" (V1). Hergebruikt `useMijnVerlof` (Story 1.2) — geen tweede
 * fetch/implementatie van hetzelfde overzicht (AD-9).
 */

import { Link } from 'react-router-dom';
import { useMijnVerlof } from '../verlof';
import { StatusBadge } from '../verlof/StatusBadge';
import { formatSaldoGetal, formatJaarruimte, formatJaarruimteToelichting } from '../../lib/verlofSaldo';
import type { LeaveBalanceView } from '@functieloonhuis/shared';

/**
 * Het ene getal dat op een dashboard past: hoeveel wettelijk verlof je dit
 * jaar nog kunt plannen.
 *
 * Rendert niets wanneer er geen jaarruimte is (onbekend saldo, geen
 * contracturen, of een medewerker zonder wettelijk verlof) — een lege plek is
 * eerlijker dan een getal dat we niet kunnen onderbouwen.
 */
function PrimaireJaarruimte({ balances }: { balances: LeaveBalanceView[] }) {
  const wettelijk = balances.find((b) => b.type === 'Wettelijk verlof');
  if (!wettelijk || wettelijk.yearRemaining === null) return null;

  return (
    <div data-testid="dashboard-jaarruimte" className="mb-4 pb-4 border-b border-[#E2E8F0]">
      <p className="text-xs text-gray-500">Nog te plannen dit jaar — wettelijk verlof</p>
      <p
        className="text-3xl font-bold text-[#2F5A7A] leading-tight"
        aria-label={`${formatJaarruimte(
          wettelijk.yearRemaining,
          wettelijk.hoursPerDay,
          wettelijk.varyingSchedule
        )}, ${wettelijk.yearRemaining} uur. Verwachting als je het jaar uitwerkt.`}
      >
        {formatJaarruimte(wettelijk.yearRemaining, wettelijk.hoursPerDay, wettelijk.varyingSchedule)}
      </p>
      <p className="text-xs text-gray-400">
        {formatJaarruimteToelichting(wettelijk.yearRemaining, wettelijk.entitlementBasisDays)}
      </p>
    </div>
  );
}

export function EigenVerlofWidget() {
  const { data, isLoading, isError } = useMijnVerlof();
  const overview = data?.data;

  return (
    <div data-testid="eigen-verlof-widget" className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm">
      <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
        <h3 className="text-lg font-medium text-[#1E293B]">Mijn verlof</h3>
        <Link to="/mijn-verlof" className="text-sm text-[#54949E] hover:underline">
          Bekijk alle →
        </Link>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5A7A]"></div>
          </div>
        ) : isError ? (
          <p data-testid="eigen-verlof-error" className="text-sm text-[#D64545]">
            Kon je verlofoverzicht niet laden. Probeer het later opnieuw.
          </p>
        ) : overview?.linked === false ? (
          <p className="text-sm text-gray-500">Verlofsaldo niet beschikbaar — geen loonkoppeling</p>
        ) : (
          <>
            {/* Bewust ÉÉN getal in plaats van vier tegels met elk twee cijfers:
                acht getallen in een widget beantwoordt geen enkele vraag. Het
                wettelijk verlof is waar de zomervakantie vandaan komt; de rest
                staat op Mijn verlof. */}
            <PrimaireJaarruimte balances={overview?.balances ?? []} />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {(overview?.balances ?? []).map((balance) => (
                <div key={balance.type} data-testid="dashboard-balance-tile" className="bg-gray-50 p-3 rounded-lg text-center">
                  <p
                    className={`text-xl font-bold ${
                      balance.remaining === null ? 'text-gray-400 italic' : 'text-[#1E293B]'
                    }`}
                  >
                    {formatSaldoGetal(balance.remaining)}
                  </p>
                  <p className="text-xs text-gray-500 truncate" title={balance.type}>{balance.type}</p>
                </div>
              ))}
            </div>

            {(overview?.history ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">Nog geen aanvragen.</p>
            ) : (
              <ul className="space-y-2" data-testid="dashboard-status-mini-lijst">
                {overview!.history.slice(0, 3).map((entry) => (
                  <li key={entry.id} className="flex items-center justify-between text-sm">
                    <span className="text-[#1E293B] truncate">
                      {entry.leaveTypeName} · {entry.startDate}
                    </span>
                    <StatusBadge
                      status={entry.requestStatus}
                      hours={entry.hours}
                      displayStatus={entry.displayStatus ?? null}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
